/**
 * Which environment this bundle was built for, and the browser-chrome
 * decoration that goes with it.
 *
 * `VITE_ENV` is written into `frontend/.env` by `scripts/deploy.sh` alongside
 * the API base and Cognito ids, so it is stamped at build time like every
 * other value the SPA needs.
 *
 * The rule is fail-loud: only the exact string `prod` counts as production.
 * A typo, a missing variable, or a local `npm run dev` with no .env all read
 * as non-production and get the banner. A spurious warning in prod is noisy
 * and self-corrects the moment someone looks at it; a missing warning in dev
 * is silent, and is the whole failure this exists to prevent -- the same
 * instinct as `fintrac_f2r_endpoint` defaulting to FINTRAC's test host.
 *
 * Values are read per call rather than captured at module load, so tests can
 * set `import.meta.env.VITE_ENV` per case.
 */

const PROD_ENV = "prod";

/** The configured environment name, trimmed; empty when unset. */
export function envName(): string {
  return ((import.meta.env.VITE_ENV as string | undefined) ?? "").trim();
}

export function isProd(): boolean {
  return envName() === PROD_ENV;
}

/**
 * Whether this bundle offers the public /docs pages. Dev only (decided
 * 2026-10-05): prod has no /docs route and no Documentation sidebar item, and
 * scripts/build_portal_docs.py leaves the docs text out of a prod bundle, so
 * hiding the UI is not the only thing keeping it out.
 */
export function docsEnabled(): boolean {
  return !isProd();
}

/** Short upper-case label for the banner and the tab title. */
export function envTag(): string {
  return envName().toUpperCase() || "UNKNOWN";
}

/** Amber disc carrying the environment's initial, as an inline SVG favicon. */
function faviconHref(tag: string): string {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">` +
    `<circle cx="16" cy="16" r="15" fill="#f5b342"/>` +
    `<text x="16" y="23" text-anchor="middle" font-family="sans-serif" ` +
    `font-size="20" font-weight="bold" fill="#0a1826">${tag[0]}</text>` +
    `</svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

/**
 * Decorates the document for a non-production build: a `[DEV]`-style title
 * prefix, a distinct favicon, and a `non-prod` class on <body> that the
 * stylesheet uses to reserve the banner's height.
 *
 * Together these are what a banner cannot do -- they identify the right tab
 * in a strip of five, and they survive into bookmarks and history.
 *
 * Prod is left entirely alone, and repeat calls are a no-op.
 */
export function applyEnvBranding(doc: Document = document): void {
  if (isProd()) return;

  const tag = envTag();
  const prefix = `[${tag}] `;
  if (!doc.title.startsWith(prefix)) doc.title = prefix + doc.title;

  let link = doc.querySelector<HTMLLinkElement>("link[rel='icon']");
  if (!link) {
    link = doc.createElement("link");
    link.rel = "icon";
    doc.head.appendChild(link);
  }
  link.setAttribute("href", faviconHref(tag));

  doc.body.classList.add("non-prod");
}
